import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelpers } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage } from '@/lib/conflict';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
  Power,
  Trash2,
  Settings,
  CreditCard,
  LayoutDashboard,
  ChevronRight,
  LayoutGrid,
} from 'lucide-react';
import type { AdminTab } from '@/types';
import { PluginOverview } from '@/components/plugins/PluginOverview';
import { PluginSettings } from '@/components/plugins/PluginSettings';
import { PluginBilling } from '@/components/plugins/PluginBilling';
import { HealthBadge } from '@/components/plugins/HealthBadge';
import { getProjectUrl } from '@/lib/utils';
import { useConfirm } from '@/components/ui/confirm-provider';

// Tab icon mapping.
// NOTE: 'billing' will get a real component in Phase 3 (reseller billing per
// docs/PLUGIN-CONTRACT.md §9). For now it renders a "coming soon" placeholder.
const tabIcons: Record<string, any> = {
  overview: LayoutDashboard,
  settings: Settings,
  admin: LayoutGrid,
  billing: CreditCard,
};

// Health poll for the badge in the header. TODO: promote to qk
const pluginHealthKey = (projectId: string | undefined, pluginId: string | undefined) =>
  ['plugin-health', projectId, pluginId] as const;

export default function PluginDetail() {
  const { projectId, pluginId } = useParams<{ projectId: string; pluginId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch manifest + install status
  const { data, isLoading, error } = useQuery({
    queryKey: qk.pluginDetail(projectId, pluginId!),
    queryFn: () => apiHelpers.getPluginManifest(projectId!, pluginId!),
    enabled: Boolean(projectId) && Boolean(pluginId),
  });

  // Fetch the project to derive the storefront origin for link-card widgets
  // (e.g. SEO Co-Pilot's "Open sitemap.xml" / "Open llms.txt" cards must
  // resolve against the customer's live custom domain, NOT admin.tyashin.com).
  const { data: projectData } = useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => apiHelpers.getProject(projectId!),
    enabled: Boolean(projectId),
  });
  const project = projectData?.data?.data;
  const storefrontOrigin = project ? getProjectUrl(project as any) : null;

  // Fetch health (only if manifest says to show it)
  const manifest = data?.data?.data?.manifest;
  const installed = data?.data?.data?.installed;
  const usage = data?.data?.data?.usage;

  const { data: healthData } = useQuery({
    queryKey: pluginHealthKey(projectId, pluginId),
    queryFn: () => apiHelpers.getPluginHealth(projectId!, pluginId!),
    enabled: Boolean(manifest?.adminPage?.showHealthStatus) && Boolean(installed),
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 min
  });

  const health = healthData?.data?.data;

  /**
   * Predicate-based invalidation that catches every plugin query key
   * in the admin app (manifest, installed list, project plugins, health).
   * Pre-fix the uninstall mutation only invalidated `plugin-manifest`,
   * leaving the Plugins dashboard + Project Detail page rendering the
   * stale "still installed" state.
   */
  const invalidateAllPluginQueries = () =>
    // Match any query whose key contains "plugin" anywhere in any
    // segment. Earlier `first.startsWith('plugin')` missed the
    // `qk.projectPlugins()` (`['project-plugins', ...]`) keys used by
    // the dashboards, so the parent lists kept showing stale state
    // after uninstall until manual refresh.
    queryClient.invalidateQueries({
      predicate: (q) =>
        q.queryKey.some((seg) => typeof seg === 'string' && seg.toLowerCase().includes('plugin')),
    });

  // Toggle enabled/disabled
  const toggleMutation = useMutation({
    mutationFn: () => apiHelpers.togglePlugin(projectId!, pluginId!),
    onSuccess: () => {
      invalidateAllPluginQueries();
      toast.success(installed?.isEnabled ? 'Plugin disabled' : 'Plugin enabled');
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Failed to toggle plugin'));
    },
  });

  // Uninstall
  const uninstallMutation = useMutation({
    mutationFn: () => apiHelpers.uninstallPlugin(projectId!, pluginId!),
    onSuccess: () => {
      toast.success('Plugin removed');
      invalidateAllPluginQueries();
      // Navigate away — but only AFTER the toast paints + caches bust,
      // so the user actually sees the confirmation before the route swap.
      setTimeout(() => navigate(`/projects/${projectId}`), 50);
    },
    onError: (err) => {
      // Without this, "Remove plugin" was a silent no-op on any backend
      // error (401 expired JWT, 500, network) — looked completely broken.
      toast.error(apiErrorMessage(err, 'Failed to remove plugin'), { duration: 8000 });
    },
  });

  // Set default active tab from manifest
  useEffect(() => {
    if (manifest?.adminPage?.tabs?.length) {
      setActiveTab(manifest.adminPage.tabs[0].id);
    }
  }, [manifest]);

  // Handle custom tab navigation
  const handleTabClick = (tab: AdminTab) => {
    if (tab.type === 'custom' && tab.redirectPath) {
      navigate(`/projects/${projectId}${tab.redirectPath}`);
    } else {
      setActiveTab(tab.id);
    }
  };

  // Interpolate URLs with project data + any connectionMeta stored on installed.config
  const interpolateUrl = (url: string) => {
    let out = url.replace('{projectId}', projectId || '').replace('{pluginId}', pluginId || '');
    // Substitute any {key} placeholder from installed config (e.g. {yomProjectId})
    const cfg = (installed?.config ?? {}) as Record<string, unknown>;
    for (const key of Object.keys(cfg)) {
      const v = cfg[key];
      if (typeof v === 'string') {
        out = out.replace(`{${key}}`, v);
      }
    }
    return out;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !manifest) {
    return (
      <div className="py-24 text-center">
        <p className="text-muted-foreground">Plugin not found or failed to load.</p>
        <button
          onClick={() => navigate(`/projects/${projectId}/plugins`)}
          className="text-primary mt-4 text-sm hover:underline"
        >
          Back to Plugins
        </button>
      </div>
    );
  }

  // Compose tabs: start with manifest tabs, inject a synthetic "Admin" tab when the
  // plugin declares endpoints.admin (contract-compliant plugins per PLUGIN-CONTRACT.md §5).
  const manifestTabs = manifest.adminPage?.tabs || [];
  const hasContractAdmin = Boolean(manifest.endpoints?.admin);
  const adminTab: AdminTab | null = hasContractAdmin
    ? { id: 'admin', label: 'Admin', type: 'admin', icon: 'dashboard' }
    : null;
  const tabs: AdminTab[] = adminTab
    ? [...manifestTabs.filter((t) => t.id !== 'admin'), adminTab]
    : manifestTabs;

  const dashboardUrl = manifest.links?.dashboard ? interpolateUrl(manifest.links.dashboard) : null;
  const adminEndpoint = manifest.endpoints?.admin ? interpolateUrl(manifest.endpoints.admin) : null;
  const embedSupport = manifest.embedSupport || 'none';

  // Plugin type badge color
  const typeBadgeClass =
    manifest.pluginType === 'aapastech-service'
      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
      : manifest.pluginType === 'third-party'
        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
        : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';

  const typeLabel =
    manifest.pluginType === 'aapastech-service'
      ? 'Aapastech'
      : manifest.pluginType === 'third-party'
        ? 'Third-party'
        : 'Built-in';

  return (
    <div className="space-y-6">
      {/* ── Back button ─────────────────────────────────────── */}
      {/* Explicit parent, not navigate(-1): custom plugin pages (SEO dashboard
          etc.) push this page back onto the history, so history-back loops
          detail ↔ dashboard and never reaches the plugins list. */}
      <button
        onClick={() => navigate(`/projects/${projectId}/plugins`)}
        className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Plugins
      </button>

      {/* ── Header ──────────────────────────────────────────── */}
      <div className="bg-card rounded-xl border p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 flex h-14 w-14 items-center justify-center rounded-xl">
              <span className="text-primary material-symbols-outlined text-2xl">
                {manifest.icon || 'extension'}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold">{manifest.name}</h1>
                <span className="text-muted-foreground rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-800">
                  v{manifest.version}
                </span>
                <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${typeBadgeClass}`}>
                  {typeLabel}
                </span>
              </div>
              <p className="text-muted-foreground mt-0.5 text-sm">{manifest.description}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Health badge */}
            {manifest.adminPage?.showHealthStatus && health && (
              <HealthBadge status={health.status} message={health.message} />
            )}

            {/* Dashboard link */}
            {dashboardUrl && (
              <a
                href={dashboardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-muted hover:bg-muted/80 flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Dashboard
              </a>
            )}

            {/* Toggle */}
            {installed && (
              <button
                onClick={() => toggleMutation.mutate()}
                disabled={toggleMutation.isPending}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  installed.isEnabled
                    ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
                }`}
              >
                <Power className="h-4 w-4" />
                {installed.isEnabled ? 'Enabled' : 'Disabled'}
              </button>
            )}

            {/* Uninstall */}
            {installed && (
              <button
                onClick={async () => {
                  if (
                    await confirm({
                      title: 'Uninstall plugin?',
                      message: `Uninstall ${manifest.name}? This cannot be undone.`,
                      danger: true,
                    })
                  ) {
                    uninstallMutation.mutate();
                  }
                }}
                disabled={uninstallMutation.isPending}
                className="rounded-lg px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                {uninstallMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Tab bar ─────────────────────────────────────────── */}
      {tabs.length > 1 && (
        <div className="border-b">
          <nav className="-mb-px flex gap-1 overflow-x-auto px-1">
            {tabs.map((tab) => {
              const Icon = tabIcons[tab.type] || ChevronRight;
              const isActive = tab.type !== 'custom' && activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab)}
                  className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-primary text-primary'
                      : 'text-muted-foreground hover:text-foreground border-transparent'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                  {tab.type === 'custom' && <ExternalLink className="h-3 w-3 opacity-50" />}
                </button>
              );
            })}
          </nav>
        </div>
      )}

      {/* ── Tab content ─────────────────────────────────────── */}
      <div>
        {activeTab === 'overview' && (
          <PluginOverview
            manifest={manifest}
            installed={installed ?? null}
            health={health}
            projectId={projectId!}
            pluginId={pluginId!}
            storefrontOrigin={storefrontOrigin}
            usage={usage}
            onNavigateToPlans={() => {
              const plansTab = tabs.find((t) => t.type === 'billing');
              if (plansTab) setActiveTab(plansTab.id);
            }}
          />
        )}
        {activeTab === 'settings' && installed && (
          <PluginSettings
            manifest={manifest}
            config={installed.config}
            projectId={projectId!}
            pluginId={pluginId!}
          />
        )}
        {(activeTab === 'billing' || activeTab === 'plans') && (
          <PluginBilling
            manifest={manifest}
            projectId={projectId!}
            pluginId={pluginId!}
            usage={usage}
          />
        )}
        {activeTab === 'admin' && adminEndpoint && embedSupport === 'iframe' && (
          <div className="bg-card overflow-hidden rounded-xl border">
            <iframe
              src={adminEndpoint}
              title={`${manifest.name} admin`}
              className="h-[calc(100vh-280px)] w-full"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          </div>
        )}
        {activeTab === 'admin' && adminEndpoint && embedSupport === 'external' && (
          <div className="bg-card rounded-xl border p-10 text-center">
            <ExternalLink className="text-muted-foreground mx-auto mb-3 h-10 w-10" />
            <h3 className="font-semibold">Managed in {manifest.name}</h3>
            <p className="text-muted-foreground mx-auto mt-1 max-w-md text-sm">
              {manifest.name} is configured in its own dashboard. Changes sync to Tyashin
              automatically.
            </p>
            <a
              href={adminEndpoint}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary text-primary-foreground hover:bg-primary/90 mt-5 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
            >
              Open {manifest.name} Dashboard
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        )}
        {activeTab === 'admin' && (!adminEndpoint || embedSupport === 'none') && (
          <div className="bg-card rounded-xl border p-8 text-center">
            <p className="text-muted-foreground text-sm">
              This plugin does not expose an admin UI.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
